import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  Grid,
  MenuItem,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import AppToast from "../../components/AppToast";
import MDEditor, { commands } from '@uiw/react-md-editor';
import ImagePreviewModal from "../../components/ui/ImagePreviewModal";
import useFormPersistence from "../../hooks/useFormPersistence";

export default function AddMom() {
  const { projectId } = useParams();
  const { token, userData } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState(null);

  const [reportMode, setReportMode] = useState(""); // "NEW" or "CARRYFORWARD"
  const [oldMoms, setOldMoms] = useState([]);
  const [selectedOldMomId, setSelectedOldMomId] = useState("");

  const persistenceKey = `mom-report-draft-${projectId || 'new'}`;
  const [formState, setFormState, clearPersistenceDraft, isDraftLoaded] = useFormPersistence(persistenceKey, {
    showModeModal: true,
    formData: {
      meetingDate: new Date().toISOString().split("T")[0],
      discussionPoints: [],
      status: "DRAFT",
    },
    isAddingPoint: false,
    editingIndex: null,
    currentPoint: {
      pointDate: new Date().toISOString().split("T")[0],
      discussion: "",
      instructionImagesFiles: [],
      actionable: userData?.name || "",
      responsibility: "",
      status: "Open",
      closeDate: "",
      remarks: "",
    }
  }, { useIndexedDB: true });

  const { showModeModal, formData, isAddingPoint, editingIndex, currentPoint } = formState;

  const setShowModeModal = (val) => setFormState(prev => ({ ...prev, showModeModal: typeof val === 'function' ? val(prev.showModeModal) : val }));
  const setFormData = (val) => setFormState(prev => ({ ...prev, formData: typeof val === 'function' ? val(prev.formData) : val }));
  const setIsAddingPoint = (val) => setFormState(prev => ({ ...prev, isAddingPoint: typeof val === 'function' ? val(prev.isAddingPoint) : val }));
  const setEditingIndex = (val) => setFormState(prev => ({ ...prev, editingIndex: typeof val === 'function' ? val(prev.editingIndex) : val }));
  const setCurrentPoint = (val) => setFormState(prev => ({ ...prev, currentPoint: typeof val === 'function' ? val(prev.currentPoint) : val }));

  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch project details and old MOMs
  useEffect(() => {
    const fetchData = async () => {
      try {
        const config = { headers: { Authorization: token ? `Bearer ${token}` : undefined } };
        const [projectRes, momsRes] = await Promise.all([
          axios.get(`${url}/api/inhouse-projects/fetch-by/${projectId}`, config),
          axios.get(`${url}/api/mom/project/${projectId}`, config)
        ]);
        
        if (projectRes.data.project) setProject(projectRes.data.project);
        if (momsRes.data.moms) {
          const fetchedMoms = momsRes.data.moms;
          setOldMoms(fetchedMoms);
          
          if (fetchedMoms.length > 0) {
            setReportMode("CARRYFORWARD");
            setSelectedOldMomId(fetchedMoms[fetchedMoms.length - 1]._id);
          } else {
            setReportMode("NEW");
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setToast({ open: true, type: "error", title: "Error", message: "Failed to load project data" });
      }
    };
    if (projectId && token) fetchData();
  }, [projectId, token]);

  const handleProceed = () => {
    if (reportMode === "CARRYFORWARD" && !selectedOldMomId) {
      setToast({ open: true, type: "warning", title: "Warning", message: "Please select a report to carryforward" });
      return;
    }

    if (reportMode === "CARRYFORWARD") {
      const selectedMom = oldMoms.find((m) => m._id === selectedOldMomId);
      if (selectedMom && selectedMom.discussionPoints) {
        // Filter open points
        const openPoints = selectedMom.discussionPoints.filter(
          (p) => p.status === "Open" || p.status === "Pending" || p.status === "In Progress"
        );
        
        const newPoints = openPoints.map((p, index) => ({
          pointNumber: index + 1,
          pointDate: new Date().toISOString().split("T")[0],
          discussion: p.discussion || p.actionable || p.topic || "",
          instructionImagesFiles: [],
          actionable: p.actionable || p.actionBy || "",
          responsibility: p.responsibility || p.givenBy || "",
          status: "Open",
          closeDate: "",
          remarks: p.remarks || "",
        }));

        setFormData((prev) => ({
          ...prev,
          discussionPoints: newPoints
        }));
      }
    } else {
      // NEW
      setFormData((prev) => ({
        ...prev,
        discussionPoints: []
      }));
    }

    setShowModeModal(false);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ----------------------------------------
  // Inline Point Editor Logic
  // ----------------------------------------
  const handleOpenAddPoint = () => {
    setCurrentPoint({
      pointDate: new Date().toISOString().split("T")[0],
      discussion: "",
      instructionImagesFiles: [],
      actionable: userData?.name || "",
      responsibility: "",
      status: "Open",
      closeDate: "",
      remarks: "",
    });
    setEditingIndex(null);
    setIsAddingPoint(true);
  };

  const handleEditPoint = (index) => {
    setCurrentPoint(formData.discussionPoints[index]);
    setEditingIndex(index);
    setIsAddingPoint(true);
  };

  const handleDeletePoint = (index) => {
    const newPoints = [...formData.discussionPoints];
    newPoints.splice(index, 1);
    // Re-number
    newPoints.forEach((p, i) => { p.pointNumber = i + 1; });
    setFormData({ ...formData, discussionPoints: newPoints });
  };

  const handleCurrentPointChange = (field, value) => {
    setCurrentPoint({ ...currentPoint, [field]: value });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const textarea = e.target;
      const start = textarea.selectionStart;
      const value = textarea.value;
      
      const beforeCursor = value.substring(0, start);
      const currentLineStart = beforeCursor.lastIndexOf('\n') + 1;
      const currentLine = beforeCursor.substring(currentLineStart);
      
      const bulletMatch = currentLine.match(/^(\s*)([-*+])\s(.*)$/);
      const numberMatch = currentLine.match(/^(\s*)(\d+)\.\s(.*)$/);
      
      if (bulletMatch || numberMatch) {
        e.preventDefault();
        
        let insertText = '';
        if (bulletMatch) {
          if (!bulletMatch[3].trim()) {
            const newValue = value.substring(0, currentLineStart) + value.substring(start);
            handleCurrentPointChange('discussion', newValue);
            setTimeout(() => {
              textarea.selectionStart = textarea.selectionEnd = currentLineStart;
            }, 0);
            return;
          }
          insertText = `\n${bulletMatch[1]}${bulletMatch[2]} `;
        } else if (numberMatch) {
          if (!numberMatch[3].trim()) {
            const newValue = value.substring(0, currentLineStart) + value.substring(start);
            handleCurrentPointChange('discussion', newValue);
            setTimeout(() => {
              textarea.selectionStart = textarea.selectionEnd = currentLineStart;
            }, 0);
            return;
          }
          const nextNum = parseInt(numberMatch[2], 10) + 1;
          insertText = `\n${numberMatch[1]}${nextNum}. `;
        }
        
        const newValue = value.substring(0, start) + insertText + value.substring(start);
        handleCurrentPointChange('discussion', newValue);
        
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + insertText.length;
        }, 0);
      }
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    const existingCount = currentPoint.instructionImagesFiles?.length || 0;
    
    if (existingCount + files.length > 5) {
      setToast({ open: true, type: "error", title: "Limit Exceeded", message: "Maximum 5 images allowed per point" });
      return;
    }
    
    try {
      const newPhotos = await Promise.all(
        files.map((file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                dataURL: reader.result,
                name: file.name,
                type: file.type,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );

      setCurrentPoint({
        ...currentPoint,
        instructionImagesFiles: [...(currentPoint.instructionImagesFiles || []), ...newPhotos]
      });
    } catch (err) {
      console.error("Error reading files:", err);
      setToast({ open: true, type: "error", title: "Error", message: "Failed to read image files" });
    }
  };

  const handleRemoveImage = (imgIndex) => {
    const newFiles = [...currentPoint.instructionImagesFiles];
    newFiles.splice(imgIndex, 1);
    setCurrentPoint({ ...currentPoint, instructionImagesFiles: newFiles });
  };

  const handleSavePoint = () => {
    if (!currentPoint.discussion) {
      setToast({ open: true, type: "warning", title: "Missing Field", message: "Instruction / Details is required" });
      return;
    }

    const newPoints = [...formData.discussionPoints];
    if (editingIndex !== null) {
      newPoints[editingIndex] = { ...currentPoint, pointNumber: editingIndex + 1 };
    } else {
      newPoints.push({ ...currentPoint, pointNumber: newPoints.length + 1 });
    }

    setFormData({ ...formData, discussionPoints: newPoints });
    setIsAddingPoint(false);
    setEditingIndex(null);
  };
  // ----------------------------------------
  // Helper function to convert base64 to blob
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleSubmit = async (submitStatus = "DRAFT") => {
    try {
      setLoading(true);
      const submitData = new FormData();
      submitData.append("projectId", projectId);
      submitData.append("meetingDate", formData.meetingDate);
      submitData.append("status", submitStatus);

      // Clean up points: remove file objects before stringifying
      const pointsToSubmit = formData.discussionPoints.map(p => {
        const { instructionImagesFiles, ...rest } = p;
        return rest;
      });
      submitData.append("discussionPoints", JSON.stringify(pointsToSubmit));

      // Append files
      formData.discussionPoints.forEach((point, index) => {
        if (point.instructionImagesFiles && point.instructionImagesFiles.length > 0) {
          point.instructionImagesFiles.forEach(photo => {
            if (photo.dataURL) {
              const fileBlob = dataURLtoBlob(photo.dataURL);
              submitData.append(`images_${index}`, fileBlob, photo.name || "image.png");
            }
          });
        }
      });

      const config = { 
        headers: { 
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "multipart/form-data"
        } 
      };

      await axios.post(`${url}/api/mom/create`, submitData, config);

      clearPersistenceDraft();

      setToast({ open: true, type: "success", title: "Success", message: `MOM Report ${submitStatus === "SUBMITTED" ? "submitted" : "saved"} successfully!` });
      setTimeout(() => {
        navigate(`/progress/edit-report/${projectId}`);
      }, 1500);
    } catch (err) {
      console.error("Error creating MOM:", err);
      setToast({ open: true, type: "error", title: "Error", message: err.response?.data?.message || "Failed to create MOM report" });
    } finally {
      setLoading(false);
    }
  };

  const currentSerialNo = oldMoms.length + 1;

  if (!isDraftLoaded) {
    return (
      <Box sx={{ p: 5, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Typography>Loading Draft...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.2, position: 'relative' }}>
      <AppToast {...toast} onClose={() => setToast({ ...toast, open: false })} />

      {/* Mode Selection Modal */}
      <Dialog open={showModeModal} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ color: COLORS.PRIMARY, fontWeight: 600 }}>Create Minutes of Meeting (MOM)</DialogTitle>
        <DialogContent dividers>
          <Box mb={3}>
            <Typography sx={{ mb: 1, fontWeight: 500 }}>Select Report Mode:</Typography>
            <Box display="flex" gap={2}>
              <Button
                variant={reportMode === "NEW" ? "contained" : "outlined"}
                onClick={() => setReportMode("NEW")}
                sx={{
                  flex: 1,
                  bgcolor: reportMode === "NEW" ? COLORS.SECONDARY : "transparent",
                  color: reportMode === "NEW" ? "#fff" : COLORS.SECONDARY,
                  borderColor: COLORS.SECONDARY,
                  "&:hover": {
                    bgcolor: reportMode === "NEW" ? COLORS.SECONDARY : "rgba(102, 51, 153, 0.04)",
                  }
                }}
              >
                New MOM
              </Button>
              <Button
                variant={reportMode === "CARRYFORWARD" ? "contained" : "outlined"}
                onClick={() => setReportMode("CARRYFORWARD")}
                disabled={oldMoms.length === 0}
                sx={{
                  flex: 1,
                  bgcolor: reportMode === "CARRYFORWARD" ? COLORS.SECONDARY : "transparent",
                  color: reportMode === "CARRYFORWARD" ? "#fff" : COLORS.SECONDARY,
                  borderColor: COLORS.SECONDARY,
                  "&:hover": {
                    bgcolor: reportMode === "CARRYFORWARD" ? COLORS.SECONDARY : "rgba(102, 51, 153, 0.04)",
                  }
                }}
              >
                Carryforward
              </Button>
            </Box>
          </Box>

          {reportMode === "CARRYFORWARD" && (
            <Box>
              <Typography sx={{ mb: 1, fontWeight: 500 }}>Select Previous MOM:</Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={selectedOldMomId}
                onChange={(e) => setSelectedOldMomId(e.target.value)}
              >
                {oldMoms.map((m, i) => (
                  <MenuItem key={m._id} value={m._id}>
                    MOM #{i + 1} - {new Date(m.meetingDate).toLocaleDateString()}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => navigate(-1)} color="inherit">Cancel</Button>
          <Button onClick={handleProceed} variant="contained" sx={{ bgcolor: COLORS.SECONDARY }}>Proceed</Button>
        </DialogActions>
      </Dialog>

      {/* HEADER SECTION */}
      <Box sx={{ mb: 3 }}>
        {/* Row 1 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
              Minutes of Meeting (MOM)
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.SECONDARY }}>
              #{currentSerialNo}
            </Typography>
          </Box>
          <Box />
        </Box>

        {/* Row 2 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Project Name:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.projectName || "-"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT }}>
              Date:
            </Typography>
            <TextField
              type="date"
              size="small"
              value={formData.meetingDate}
              onChange={(e) => handleChange("meetingDate", e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  height: "32px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Row 3 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Address:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.siteAddress || "-"}
            </Typography>
          </Box>
          <Box />
        </Box>

        {/* Row 4 */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Developer:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.developerName || "-"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Engineer:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.assignedEngineer?.name || "-"}
            </Typography>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ mt: 2, height: "1px", backgroundColor: COLORS.LIGHT_SECONDARY, opacity: 0.7 }} />
      </Box>

      {/* ================= POINTS TABLE ================= */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>Points & Actions</Typography>
          {!isAddingPoint && (
            <Button startIcon={<Icon name="plus2" />} onClick={handleOpenAddPoint} variant="outlined" size="small" sx={{ borderColor: COLORS.SECONDARY, color: COLORS.SECONDARY }}>
              Add Point
            </Button>
          )}
        </Box>

        {formData.discussionPoints.length > 0 && (
          <TableContainer component={Paper} sx={{ boxShadow: "none", border: `1px solid ${COLORS.LIGHT_SECONDARY}`, mb: 3 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
                <TableRow>
                  <TableCell><strong>Sr No.</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Instruction</strong></TableCell>
                  <TableCell><strong>Given By</strong></TableCell>
                  <TableCell><strong>Action By</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.discussionPoints.map((p, index) => (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>{p.pointNumber}</TableCell>
                    <TableCell>{p.pointDate ? new Date(p.pointDate).toLocaleDateString() : "-"}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-wrap", maxWidth: 300 }}>
                      <Box data-color-mode="light" sx={{ color: COLORS.PRIMARY, fontSize: "14px" }}>
                        <MDEditor.Markdown source={p.discussion} style={{ whiteSpace: 'pre-wrap', backgroundColor: 'transparent', color: 'inherit', fontSize: 'inherit' }} />
                      </Box>
                      {p.instructionImagesFiles && p.instructionImagesFiles.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          {p.instructionImagesFiles.map((photo, idx) => (
                            <Box key={idx} onClick={() => setPreviewImage(photo.dataURL)} sx={{ cursor: 'pointer', width: 40, height: 40, borderRadius: 1, overflow: 'hidden', border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                              <img src={photo.dataURL} alt="instruction" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Box>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{p.responsibility || "-"}</TableCell>
                    <TableCell>{p.actionable || "-"}</TableCell>
                    <TableCell>
                      <Typography sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: p.status === "Close" ? COLORS.SUCCESS : p.status === "Open" ? COLORS.WARNING : COLORS.PRIMARY,
                      }}>
                        {p.status}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton size="small" onClick={() => handleEditPoint(index)} color="primary">
                          <Icon name="edit" size={16} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeletePoint(index)} color="error">
                          <Icon name="delete" size={16} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {formData.discussionPoints.length === 0 && !isAddingPoint && (
           <Typography sx={{ textAlign: 'center', color: COLORS.LIGHT_SECONDARY, py: 4, fontStyle: 'italic' }}>
             No points added yet. Click "Add Point" to get started.
           </Typography>
        )}

        {/* INLINE POINT EDITOR */}
        {isAddingPoint && (
          <Box sx={{ p: 3, border: `1px solid ${COLORS.SECONDARY}`, borderRadius: 2, mb: 3, bgcolor: "rgba(102, 51, 153, 0.02)", position: "relative" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} pb={1} borderBottom={`1px solid ${COLORS.LIGHT_SECONDARY}`}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
                {editingIndex !== null ? `Edit Point #${editingIndex + 1}` : "Add New Point"}
              </Typography>
              <IconButton color="error" size="small" onClick={() => { setIsAddingPoint(false); setEditingIndex(null); }}>
                <Icon name="close" />
              </IconButton>
            </Box>

            {/* Row 1: Instruction */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.TEXT, mb: 0.5 }}>
                Instruction / Details *
              </Typography>
              <Box data-color-mode="light" sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: '5px', overflow: 'hidden', '.w-md-editor': { border: 'none', borderRadius: 0 } }}>
                <MDEditor
                  value={currentPoint.discussion}
                  onChange={(val) => handleCurrentPointChange("discussion", val || "")}
                  height={200}
                  preview="edit"
                  extraCommands={[]}
                  commands={[
                    commands.bold,
                    commands.italic,
                    commands.strikethrough,
                    commands.divider,
                    commands.unorderedListCommand,
                    commands.orderedListCommand,
                    commands.checkedListCommand,
                  ]}
                  textareaProps={{
                    onKeyDown: handleKeyDown
                  }}
                />
              </Box>
            </Box>

            {/* Row 2: Attachments */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: COLORS.TEXT, mb: 1 }}>Attachments</Typography>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Button variant="outlined" component="label" size="small" startIcon={<Icon name="upload" />} sx={{ bgcolor: "#fff" }}>
                  Upload Images (Max 5)
                  <input type="file" hidden multiple accept="image/*" onChange={handleImageUpload} />
                </Button>
                {(currentPoint.instructionImagesFiles || []).map((photo, imgIdx) => (
                  <Box key={imgIdx} position="relative" width={60} height={60} borderRadius={1} overflow="hidden" border={`1px solid ${COLORS.LIGHT_SECONDARY}`}>
                    <img src={photo.dataURL} alt="preview" style={{ cursor: 'pointer', width: '100%', height: '100%', objectFit: 'cover' }} onClick={() => setPreviewImage(photo.dataURL)} />
                    <IconButton size="small" sx={{ position: 'absolute', top: 0, right: 0, p: 0.5, bgcolor: 'rgba(255,255,255,0.7)' }} onClick={(e) => { e.stopPropagation(); handleRemoveImage(imgIdx); }}>
                      <Icon name="close" size={14} color="error" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>

            <Grid container spacing={3}>
              {/* Row 3: Metadata */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Given By"
                  value={currentPoint.responsibility}
                  onChange={(e) => handleCurrentPointChange("responsibility", e.target.value)}
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", bgcolor: "#fff" } }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Action By"
                  value={currentPoint.actionable}
                  onChange={(e) => handleCurrentPointChange("actionable", e.target.value)}
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", bgcolor: "#fff" } }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={currentPoint.status}
                  onChange={(e) => handleCurrentPointChange("status", e.target.value)}
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", bgcolor: "#fff" } }}
                >
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="Close">Close</MenuItem>
                </TextField>
              </Grid>

              {currentPoint.status === "Close" && (
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Close Date"
                    type="date"
                    value={currentPoint.closeDate ? currentPoint.closeDate.split('T')[0] : ""}
                    onChange={(e) => handleCurrentPointChange("closeDate", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", bgcolor: "#fff" } }}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={currentPoint.status === "Close" ? 2 : 4}>
                <TextField
                  fullWidth
                  label="Remarks"
                  value={currentPoint.remarks}
                  onChange={(e) => handleCurrentPointChange("remarks", e.target.value)}
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", bgcolor: "#fff" } }}
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
               <Button variant="text" color="inherit" onClick={() => { setIsAddingPoint(false); setEditingIndex(null); }}>
                 Cancel
               </Button>
               <Button variant="contained" sx={{ bgcolor: COLORS.SECONDARY }} onClick={handleSavePoint}>
                 Save Point
               </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* ================= ACTIONS ================= */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 2, borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ borderColor: COLORS.LIGHT_SECONDARY, color: COLORS.TEXT }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSubmit("DRAFT")}
          disabled={loading || isAddingPoint}
          sx={{ bgcolor: COLORS.SECONDARY, "&:hover": { bgcolor: COLORS.SECONDARY }, textTransform: "none" }}
        >
          Save as Draft
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSubmit("SUBMITTED")}
          disabled={loading || isAddingPoint}
          sx={{ bgcolor: COLORS.PRIMARY, "&:hover": { bgcolor: COLORS.PRIMARY }, textTransform: "none" }}
        >
          Submit MOM
        </Button>
      </Box>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage}
        title="Image Preview"
      />
    </Box>
  );
}
